/* RESPONSIBLE TEAM: team-inbox-experience */

import type Session from 'embercom/services/session';

// Note: We intentionally normalize to the base language (e.g., `fr-FR` -> `fr`) because
// translation payloads for real-time message blocks are keyed by base language.
// Exception: Preserve composite locales that Intercom supports: de-form, fa-IR, pt-BR, zh-CN, zh-TW

export function normalizeLocale(code?: string): string | undefined {
  if (!code) {
    return undefined;
  }
  let compositeLocales = ['de-form', 'fa-IR', 'pt-BR', 'zh-CN', 'zh-TW'];
  if (compositeLocales.includes(code)) {
    return code;
  }
  return code.toLowerCase().split(/[-_]/)[0];
}

export function getDefaultWorkspaceLocale(session: Session): string | undefined {
  // Workspace may not be loaded at first render; return undefined and let callers fall back
  if (!session.isWorkspaceLoaded) {
    return undefined;
  }
  let raw = session.workspace.locale;
  return normalizeLocale(typeof raw === 'string' ? raw : undefined);
}

export type BuildCycleOrderParams = {
  available: string[]; // language codes that exist in translations
  preferred?: string; // admin preferred locale (may be undefined)
  defaultLang?: string; // app.locale (workspace default)
  original?: string; // original language of the part
};

export function buildCycleOrder({
  available,
  preferred,
  defaultLang,
  original,
}: BuildCycleOrderParams): string[] {
  let order: string[] = [];

  let normPreferred = normalizeLocale(preferred);
  let normDefault = normalizeLocale(defaultLang);
  let normOriginal = normalizeLocale(original);

  if (normPreferred && normOriginal && normPreferred === normOriginal) {
    order.push(normOriginal);
  } else if (normPreferred && available.includes(normPreferred)) {
    order.push(normPreferred);
  }

  if (normDefault && available.includes(normDefault) && !order.includes(normDefault)) {
    order.push(normDefault);
  }

  if (normOriginal && !order.includes(normOriginal)) {
    order.push(normOriginal);
  }

  for (let code of available) {
    if (!order.includes(code)) {
      order.push(code);
    }
  }

  return order;
}

export function resolveCurrentSelected(
  selectedLangCode: string | undefined,
  order: string[],
): string | undefined {
  let selected = selectedLangCode ?? order[0];
  return selected;
}

export function nextInCycle(current: string | undefined, order: string[]): string | undefined {
  if (order.length === 0) {
    return undefined;
  }
  let normCurrent = normalizeLocale(current) || '';
  let idx = order.indexOf(normCurrent);
  let nextIdx = idx >= 0 ? (idx + 1) % order.length : 0;
  return order[nextIdx];
}
